import { useDispatch } from 'react-redux';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useId, useState } from 'react';
import {
  CircleDollarSign,
  ChevronLeft,
  ChevronRight,
  Headset,
  MessageCircleQuestion,
  User,
  LogOut
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LuLayoutDashboard,
  LuSettings,
} from 'react-icons/lu';
import { HiOutlineMenu } from 'react-icons/hi';
import { TbTable } from "react-icons/tb";
import { TbPlugConnected } from "react-icons/tb";
import { Sidebar, useSidebar } from 'components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { loginProfileCreatedReset } from '../Login/loginActions';
import {
  MorphPopover,
  MorphPopoverContent,
  MorphPopoverTrigger,
} from 'components/ui/popover';
import { Tooltip } from 'components/ui/tooltip';
import './sidebar.css';

const navigation = [
  { label: 'Dashboard', href: '/user/dashboard', icon: LuLayoutDashboard},
  { label: 'Leads', href: '/user/data-table', icon: TbTable },
  { label: 'Integrations', href: '/user/integration', icon: TbPlugConnected },
  { label: 'Settings', href: '/user/settings', icon: LuSettings },
];

const getMobilePageLabel = (pathname) => {
  if (pathname === '/user/dashboard') return 'Dashboard';
  if (pathname === '/user/data-table') return 'Leads';
  if (['/user/settings', '/user/change-password', '/user/billing', '/user/subscription'].includes(pathname)) {
    return 'Settings';
  }
  if (pathname === '/user/integration') return 'Integration';

  return 'Dashboard';
};

const getUserDisplayName = () => {
  const firstName = localStorage.getItem('user_first_name');
  const lastName = localStorage.getItem('user_last_name');

  return (
    [firstName, lastName].filter(Boolean).join(' ') ||
    localStorage.getItem('user_default_email') ||
    'User'
  );
};

const getUserEmail = () => {
  const defaultEmail = localStorage.getItem('user_default_email');
  if (defaultEmail) return defaultEmail;

  const storedEmails = localStorage.getItem('user_emails');
  if (!storedEmails) return 'No email available';

  try {
    const parsedEmails = JSON.parse(storedEmails);
    if (Array.isArray(parsedEmails)) return parsedEmails[0] || 'No email available';
    if (typeof parsedEmails === 'string') return parsedEmails;
  } catch {
    return storedEmails;
  }

  return 'No email available';
};

const SIDEBAR_HOVER_TRANSITION = {
  type: 'spring',
  stiffness: 360,
  damping: 32,
  mass: 0.6,
};

const SIDEBAR_LABEL_ENTER_TRANSITION = {
  duration: 0.2,
  delay: 0.08,
  ease: [0.16, 1, 0.3, 1],
};

const SIDEBAR_LABEL_EXIT_TRANSITION = {
  duration: 0.12,
  ease: [0.16, 1, 0.3, 1],
};

const MotionLink = motion(Link);

export function DashboardSidebar() {
  const {
    isMobile,
    openMobile,
    setOpenMobile,
    state,
    toggleSidebar,
  } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduceMotion = useReducedMotion() ?? false;
  const hoverLayoutId = useId();
  const accountHoverLayoutId = useId();
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [hoveredAccountItem, setHoveredAccountItem] = useState(null);
  const sidebarOpen = isMobile ? openMobile : state === 'expanded';
  const userFullName = getUserDisplayName();
  const navigateFromSidebar = (path) => {
    navigate(path);
    if (isMobile) {
      setOpenMobile(false);
    }
  };
  const openSupport = () => {
    if (window.anw2 && typeof window.anw2.open === 'function') {
      window.anw2.open();
    } else {
      document.getElementById('anw2-launcher-button-GEHlG5d1azeJzDWtf5WkgQ')?.click();
    }

    if (isMobile) {
      setOpenMobile(false);
    }
  };
  const logoSource =
    !isMobile && state === 'collapsed'
      ? `${process.env.PUBLIC_URL}/icon.png`
      : "https://app.groupboss.io/static/media/logo.d96bc6c1f4b02f704ac1.png";
  const accountLabelTransition = reduceMotion
    ? { duration: 0 }
    : sidebarOpen
      ? SIDEBAR_LABEL_ENTER_TRANSITION
      : SIDEBAR_LABEL_EXIT_TRANSITION;
  const withCollapsedTooltip = (label, element) => (
    sidebarOpen ? element : (
      <Tooltip content={label} side="right">
        {element}
      </Tooltip>
    )
  );

  return (
    <Sidebar className="dashboard-sidebar" collapsible="icon">
      <div className="dashboard-sidebar-shell">
        <Link
          aria-label="Groupboss dashboard"
          className="dashboard-sidebar-brand"
          to="https://www.groupboss.io">
          <img loading="lazy"
            alt="Groupboss"
            className="dashboard-sidebar-logo"
            height="32"
            src={logoSource}
            width="161"
          />
        </Link>

        <nav
          aria-label="Primary"
          className="dashboard-sidebar-nav"
          onMouseLeave={() => setHoveredNavItem(null)}>
          {navigation.map((item) => {
            const active =
              (item.label === 'Dashboard' && location.pathname === '/user/dashboard') ||
              (item.label === 'Leads' && location.pathname === '/user/data-table') ||
              (item.label === 'Settings' &&
                ['/user/settings', '/user/change-password', '/user/billing', '/user/subscription'].includes(location.pathname));
              const integrationActive = item.label === 'Integrations' && location.pathname === '/user/integration';

            const labelTransition = reduceMotion
              ? { duration: 0 }
              : sidebarOpen
                ? SIDEBAR_LABEL_ENTER_TRANSITION
                : SIDEBAR_LABEL_EXIT_TRANSITION;

            const navLink = (
              <MotionLink
                aria-current={active || integrationActive ? 'page' : undefined}
                aria-label={!sidebarOpen ? item.label : undefined}
                className={`dashboard-sidebar-nav-item${active || integrationActive ? ' is-active' : ''}`}
                to={item.href}
                key={item.label}
                onMouseEnter={() => setHoveredNavItem(item.label)}
                onClick={() => setOpenMobile(false)}>
                <AnimatePresence initial={false}>
                  {hoveredNavItem === item.label && (
                    <motion.span
                      animate={reduceMotion ? { opacity: 1 } : { opacity: 1, filter: 'blur(0px)' }}
                      className="dashboard-sidebar-nav-hover"
                      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, filter: 'blur(6px)' }}
                      initial={reduceMotion ? false : { opacity: 0, filter: 'blur(6px)' }}
                      layoutId={`dashboard-sidebar-hover-${hoverLayoutId}`}
                      transition={reduceMotion ? { duration: 0 } : SIDEBAR_HOVER_TRANSITION}
                    />
                  )}
                </AnimatePresence>
                <span className="dashboard-sidebar-nav-icon">
                  <item.icon aria-hidden="true" focusable="false" />
                </span>
                <motion.span
                  animate={{ opacity: sidebarOpen ? 1 : 0, x: sidebarOpen ? 0 : -4 }}
                  aria-hidden={!sidebarOpen}
                  className="dashboard-sidebar-nav-label"
                  initial={false}
                  transition={labelTransition}>
                  {item.label}
                </motion.span>
              </MotionLink>
            );

            return sidebarOpen ? navLink : (
              <Tooltip content={item.label} side="right" key={item.label}>
                {navLink}
              </Tooltip>
            );
          })}
        </nav>

        <div className="dashboard-sidebar-account">
          {withCollapsedTooltip('Support', (
          <motion.button
            aria-label={!sidebarOpen ? 'Support' : undefined}
            className="dashboard-sidebar-account-trigger"
            onClick={openSupport}
            onMouseEnter={() => setHoveredAccountItem('support')}
            onMouseLeave={() => setHoveredAccountItem(null)}
            transition={SIDEBAR_HOVER_TRANSITION}
            type="button"
            whileTap={reduceMotion ? undefined : { scale: 0.98 }}>
            <AnimatePresence initial={false}>
              {hoveredAccountItem === 'support' && (
                <motion.span
                  animate={reduceMotion ? { opacity: 1 } : { opacity: 1, filter: 'blur(0px)' }}
                  className="dashboard-sidebar-account-hover"
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, filter: 'blur(6px)' }}
                  initial={reduceMotion ? false : { opacity: 0, filter: 'blur(6px)' }}
                  layoutId={"dashboard-sidebar-account-hover-" + accountHoverLayoutId}
                  transition={reduceMotion ? { duration: 0 } : SIDEBAR_HOVER_TRANSITION}
                />
              )}
            </AnimatePresence>
            <Headset aria-hidden="true" />
            <motion.span
              animate={{ opacity: sidebarOpen ? 1 : 0, x: sidebarOpen ? 0 : -4 }}
              aria-hidden={!sidebarOpen}
              className="dashboard-sidebar-account-label"
              initial={false}
              transition={accountLabelTransition}>
              Support
            </motion.span>
          </motion.button>
          ))}

          <DropdownMenu>
            {withCollapsedTooltip('Help', (
            <DropdownMenuTrigger
              aria-label={!sidebarOpen ? 'Help' : undefined}
              className="dashboard-sidebar-account-trigger"
              onMouseEnter={() => setHoveredAccountItem('help')}
              onMouseLeave={() => setHoveredAccountItem(null)}>
              <AnimatePresence initial={false}>
                {hoveredAccountItem === 'help' && (
                  <motion.span
                    animate={reduceMotion ? { opacity: 1 } : { opacity: 1, filter: 'blur(0px)' }}
                    className="dashboard-sidebar-account-hover"
                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, filter: 'blur(6px)' }}
                    initial={reduceMotion ? false : { opacity: 0, filter: 'blur(6px)' }}
                    layoutId={`dashboard-sidebar-account-hover-${accountHoverLayoutId}`}
                    transition={reduceMotion ? { duration: 0 } : SIDEBAR_HOVER_TRANSITION}
                  />
                )}
              </AnimatePresence>
              <MessageCircleQuestion aria-hidden="true" />
              <motion.span
                animate={{ opacity: sidebarOpen ? 1 : 0, x: sidebarOpen ? 0 : -4 }}
                aria-hidden={!sidebarOpen}
                className="dashboard-sidebar-account-label"
                initial={false}
                transition={accountLabelTransition}>
                Help
              </motion.span>
            </DropdownMenuTrigger>
            ))}
            <DropdownMenuContent
              align="end"
              className="dashboard-sidebar-account-menu"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={10}>
              <DropdownMenuItem
                render={
                  <a
                    href="https://support.groupboss.io/"
                    rel="noreferrer"
                    target="_blank">
                    Docs / FAQ
                  </a>
                } />
              <DropdownMenuItem
                render={
                  <a
                    href="https://www.youtube.com/channel/UCkrO5Eod4EiIzC3P_fZScwg?sub_confirmation=1"
                    rel="noreferrer"
                    target="_blank">
                    Videos
                  </a>
                } />
              <DropdownMenuItem
                render={
                  <a
                    href="https://web.facebook.com/groups/groupboss.io/?_rdc=1&_rdr"
                    rel="noreferrer"
                    target="_blank">
                    Join Our Community
                  </a>
                } />
              <DropdownMenuItem
                render={
                  <a
                    href="https://groupboss.io/partner"
                    rel="noreferrer"
                    target="_blank">
                    Join Affiliate Program
                  </a>
                } />
              <DropdownMenuItem
                render={
                  <a
                    href="https://groupboss.io/group-manage-program"
                    rel="noreferrer"
                    target="_blank">
                    Groupmanage Program
                  </a>
                } />
            </DropdownMenuContent>
          </DropdownMenu>

          {withCollapsedTooltip('Refer and Earn', (
          <motion.a
            aria-label={!sidebarOpen ? 'Refer and Earn' : undefined}
            className="dashboard-sidebar-account-trigger"
            href="https://groupboss.io/affiliate/"
            onClick={() => isMobile && setOpenMobile(false)}
            onMouseEnter={() => setHoveredAccountItem('refer')}
            onMouseLeave={() => setHoveredAccountItem(null)}
            rel="noreferrer"
            target="_blank"
            transition={SIDEBAR_HOVER_TRANSITION}
            whileTap={reduceMotion ? undefined : { scale: 0.98 }}>
            <AnimatePresence initial={false}>
              {hoveredAccountItem === 'refer' && (
                <motion.span
                  animate={reduceMotion ? { opacity: 1 } : { opacity: 1, filter: 'blur(0px)' }}
                  className="dashboard-sidebar-account-hover"
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, filter: 'blur(6px)' }}
                  initial={reduceMotion ? false : { opacity: 0, filter: 'blur(6px)' }}
                  layoutId={`dashboard-sidebar-account-hover-${accountHoverLayoutId}`}
                  transition={reduceMotion ? { duration: 0 } : SIDEBAR_HOVER_TRANSITION}
                />
              )}
            </AnimatePresence>
            <CircleDollarSign aria-hidden="true" />
            <motion.span
              animate={{ opacity: sidebarOpen ? 1 : 0, x: sidebarOpen ? 0 : -4 }}
              aria-hidden={!sidebarOpen}
              className="dashboard-sidebar-account-label"
              initial={false}
              transition={accountLabelTransition}>
              Refer and Earn
            </motion.span>
          </motion.a>
          ))}

          {withCollapsedTooltip('Download', (
          <motion.a
            aria-label={!sidebarOpen ? 'Download' : undefined}
            className="dashboard-sidebar-account-trigger"
            href="https://chrome.google.com/webstore/detail/groupboss/gakcpcoikgklfbajjkdaomcfkpeiobfl?hl=en-US"
            onClick={() => isMobile && setOpenMobile(false)}
            onMouseEnter={() => setHoveredAccountItem('download')}
            onMouseLeave={() => setHoveredAccountItem(null)}
            rel="noreferrer"
            target="_blank"
            transition={SIDEBAR_HOVER_TRANSITION}
            whileTap={reduceMotion ? undefined : { scale: 0.98 }}>
            <AnimatePresence initial={false}>
              {hoveredAccountItem === 'download' && (
                <motion.span
                  animate={reduceMotion ? { opacity: 1 } : { opacity: 1, filter: 'blur(0px)' }}
                  className="dashboard-sidebar-account-hover"
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, filter: 'blur(6px)' }}
                  initial={reduceMotion ? false : { opacity: 0, filter: 'blur(6px)' }}
                  layoutId={`dashboard-sidebar-account-hover-${accountHoverLayoutId}`}
                  transition={reduceMotion ? { duration: 0 } : SIDEBAR_HOVER_TRANSITION}
                />
              )}
            </AnimatePresence>
            <motion.svg
              animate={{ scale: hoveredAccountItem === 'download' ? 1.08 : 1 }}
              aria-hidden="true"
              className="dashboard-sidebar-download-icon"
              fill="currentColor"
              height="256"
              role="img"
              stroke="currentColor"
              strokeWidth="0"
              transition={reduceMotion ? { duration: 0 } : SIDEBAR_HOVER_TRANSITION}
              viewBox="0 0 24 24"
              width="256"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M0 1.637v19.09c0 .9.736 1.636 1.636 1.636h.131a10.4 10.4 0 0 1-.13-1.636 10.3 10.3 0 0 1 1.667-5.64l4.202 7.276h1.128A3.77 3.77 0 0 1 12 16.958a3.77 3.77 0 0 1 3.366 5.406h1.048a4.7 4.7 0 0 0-1.587-5.406h6.83a10.34 10.34 0 0 1 .577 5.406h.13c.9 0 1.636-.737 1.636-1.637V1.637Zm9.273 2.181h5.454a1.09 1.09 0 1 1 0 2.182H9.273a1.09 1.09 0 1 1 0-2.182M12 10.364a10.36 10.36 0 0 1 9.233 5.652H12a4.71 4.71 0 0 0-4.677 4.149L3.91 14.25A10.34 10.34 0 0 1 12 10.364" />
            </motion.svg>
            <motion.span
              animate={{ opacity: sidebarOpen ? 1 : 0, x: sidebarOpen ? 0 : -4 }}
              aria-hidden={!sidebarOpen}
              className="dashboard-sidebar-account-label"
              initial={false}
              transition={accountLabelTransition}>
              Download
            </motion.span>
          </motion.a>
          ))}


          <MorphPopover>
            {withCollapsedTooltip('Profile', (
            <MorphPopoverTrigger>
              <button
                aria-label={`Profile for ${userFullName}`}
                className="dashboard-sidebar-account-trigger dashboard-sidebar-profile-trigger"
                onMouseEnter={() => setHoveredAccountItem('profile')}
                onMouseLeave={() => setHoveredAccountItem(null)}
                type="button">
                <AnimatePresence initial={false}>
                  {hoveredAccountItem === 'profile' && (
                    <motion.span
                      animate={reduceMotion ? { opacity: 1 } : { opacity: 1, filter: 'blur(0px)' }}
                      className="dashboard-sidebar-account-hover"
                      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, filter: 'blur(6px)' }}
                      initial={reduceMotion ? false : { opacity: 0, filter: 'blur(6px)' }}
                      layoutId={`dashboard-sidebar-account-hover-${accountHoverLayoutId}`}
                      transition={reduceMotion ? { duration: 0 } : SIDEBAR_HOVER_TRANSITION}
                    />
                  )}
                </AnimatePresence>
                <User aria-hidden="true" />
                <motion.span
                  animate={{ opacity: sidebarOpen ? 1 : 0, x: sidebarOpen ? 0 : -4 }}
                  aria-hidden={!sidebarOpen}
                  className="dashboard-sidebar-account-label"
                  initial={false}
                  transition={accountLabelTransition}>
                  Profile
                </motion.span>
                <motion.span
                  animate={{ opacity: sidebarOpen ? 1 : 0, x: sidebarOpen ? 0 : 4 }}
                  aria-hidden="true"
                  className="dashboard-sidebar-profile-arrow"
                  initial={false}
                  transition={reduceMotion ? { duration: 0 } : SIDEBAR_HOVER_TRANSITION}>
                  <ChevronRight aria-hidden="true" />
                </motion.span>
              </button>
            </MorphPopoverTrigger>
            ))}
            <MorphPopoverContent
              align="end"
              className="dashboard-sidebar-profile-popover"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={10}>
              <div className="dashboard-sidebar-profile-popover-user">
                <span className="dashboard-sidebar-profile-popover-email">{getUserEmail()}</span>
              </div>
              <button
                className="dashboard-sidebar-profile-logout"
                onClick={() => {
                  localStorage.removeItem('access_token');
                  localStorage.removeItem('refresh_token');
                  localStorage.removeItem('user_id');
                  localStorage.removeItem('user_first_name');
                  localStorage.removeItem('user_last_name');
                  localStorage.removeItem('user_emails');
                  localStorage.removeItem('user_default_email');
                  localStorage.removeItem('selected_group');
                  dispatch(loginProfileCreatedReset());
                  navigateFromSidebar('/login');
                }}
                type="button">
                <LogOut aria-hidden="true" />
                <span>Logout</span>
              </button>
            </MorphPopoverContent>
          </MorphPopover>
        </div>
      </div>

      {!isMobile && (
        <button
          aria-expanded={sidebarOpen}
          aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          className="dashboard-sidebar-toggle"
          onClick={toggleSidebar}
          type="button">
          {sidebarOpen ? <ChevronLeft aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
        </button>
      )}
    </Sidebar>
  );
}

export function DashboardMobileSidebarTrigger() {
  const { openMobile, toggleSidebar } = useSidebar();
  const location = useLocation();
  const pageLabel = getMobilePageLabel(location.pathname);

  return (
    <header className="dashboard-mobile-sidebar-navbar">
      <button
        aria-expanded={openMobile}
        aria-label={openMobile ? 'Close sidebar' : 'Open sidebar'}
        className="dashboard-mobile-sidebar-trigger"
        onClick={toggleSidebar}
        type="button">
        <HiOutlineMenu aria-hidden="true" />
      </button>
      <span className="dashboard-mobile-sidebar-title">{pageLabel}</span>
    </header>
  );
}
